import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialToken = params.get("token") || "";
  const [token, setTokenVal] = useState(initialToken);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      navigate("/admin/login", { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="reset-page">
      <header className="px-6 sm:px-12 pt-10 max-w-6xl w-full mx-auto">
        <Link to="/"><Brand size="sm" subtitle={true} /></Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80 mb-3">Password reset</p>
            <h1 className="font-display text-4xl">Set a new password</h1>
          </div>
          <div className="border border-border rounded-2xl bg-card p-7">
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              {!initialToken && (
                <div className="space-y-2">
                  <Label htmlFor="token">Reset token</Label>
                  <Input
                    id="token"
                    required
                    value={token}
                    onChange={(e) => setTokenVal(e.target.value)}
                    data-testid="reset-token-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="pw">New password</Label>
                <Input
                  id="pw"
                  type="password"
                  required
                  minLength={8}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw2">Confirm password</Label>
                <Input
                  id="pw2"
                  type="password"
                  required
                  minLength={8}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  data-testid="reset-password-confirm-input"
                />
              </div>
              {error && (
                <p
                  role="alert"
                  aria-live="polite"
                  className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2"
                  data-testid="reset-error"
                >
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="reset-submit-button"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
